import { apiFetch } from "../../_services/apiFetch";
import React from 'react';
import { Button, Col, FormControl, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import config from '../../../config';
import { redirectUnlessTooling } from '../../utils/moduleAccess';
import { isAdmin } from '../../utils/roleConstants';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';

const RECORD_TYPE = 'tooling_maintenance';
const REQUEST_LIMIT = 10000;

const REASON_FIELDS = [
    { key: 'reasonQualityIssue', label: 'Quality Issue' },
    { key: 'reasonMechanicalFailure', label: 'Mechanical Failure' },
    { key: 'reasonModification', label: 'Modification (ECO)' },
    { key: 'reasonWearTear', label: 'Wear & Tear' },
    { key: 'reasonTotalPreventiveMaintenance', label: 'Total Preventive Maintenance' },
    { key: 'reasonOthers', label: 'Others' }
];

function safeString(value) {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return text === 'null' || text === 'undefined' ? '' : text;
}

function safeNumber(value) {
    if (value === null || value === undefined || value === '') return 0;
    const numberValue = Number(value);
    return isNaN(numberValue) ? 0 : numberValue;
}

function getToolingKey(value) {
    return safeString(value).trim().toLowerCase();
}

function buildToolingLookup(body) {
    const toolings = Array.isArray(body) ? body : (body && Array.isArray(body.data) ? body.data : []);
    return toolings.reduce((lookup, tooling) => {
        const toolingKey = getToolingKey(tooling && tooling.toolingNo);
        if (toolingKey) lookup[toolingKey] = tooling;
        return lookup;
    }, {});
}

function calculateMtbfMetrics(records, jobs) {
    const maintenanceToolingIds = new Set(records.map(record => safeString(record && record.toolingId)).filter(Boolean));
    const matchingJobs = jobs.filter(job => maintenanceToolingIds.has(safeString(job && job.toolingID)));
    const plannedToolingIds = new Set(matchingJobs.map(job => safeString(job && job.toolingID)));

    return {
        totalOperatingHours: matchingJobs.reduce((total, job) => total + safeNumber(job && job.completeTime), 0),
        eligibleMaintenanceEvents: records.filter(record => plannedToolingIds.has(safeString(record && record.toolingId))).length
    };
}

function safeParseJson(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (_error) {
        return fallback;
    }
}

function approvalStatusLabel(item) {
    const approveStatus = item && item.approve_status !== null && item.approve_status !== undefined
        ? Number(item.approve_status)
        : null;
    if (approveStatus === 1) return 'Approved';
    if (approveStatus === 2) return 'Rejected';
    if (approveStatus === 0) return 'Pending Approval';
    return safeString(item && item.processStatus) || 'Process Pending';
}

function formatDateTime(value) {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : safeString(value);
}

function getReasonCategory(item) {
    const field = REASON_FIELDS.find(reason => safeString(item[reason.key]).trim());
    return field ? field.label : '';
}

function calculateDowntimeHours(item) {
    if (!item || !item.requestedAt || !item.processSubmittedAt) return '';
    const start = moment(item.requestedAt);
    const end = moment(item.processSubmittedAt);
    if (!start.isValid() || !end.isValid() || end.isBefore(start)) return '';
    return (end.diff(start, 'minutes') / 60).toFixed(2);
}

function getDowntimePeriod(records) {
    const ranges = records
        .filter(record => record
            && record.requestedAt !== null
            && record.requestedAt !== undefined
            && record.requestedAt !== ''
            && record.processSubmittedAt !== null
            && record.processSubmittedAt !== undefined
            && record.processSubmittedAt !== '')
        .map(record => ({ start: moment(record.requestedAt), end: moment(record.processSubmittedAt) }))
        .filter(range => range.start.isValid() && range.end.isValid() && !range.end.isBefore(range.start));

    if (!ranges.length) return null;
    const start = moment.min(ranges.map(range => range.start));
    const end = moment.max(ranges.map(range => range.end));
    const selectedRecordIds = records.map((record, index) => safeString(record && record.id) || `row-${index}`).sort();
    return {
        key: `${start.valueOf()}-${end.valueOf()}-${selectedRecordIds.join(',')}`,
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD')
    };
}

function formatHours(value) {
    return Number.isFinite(value) && value >= 0 ? `${value.toFixed(2)} hrs` : '0 hrs';
}

function calculateSparesCost(item) {
    const repairActions = safeParseJson(item && item.repairActions, []);
    if (!Array.isArray(repairActions)) return 0;
    return repairActions.reduce((total, action) => total + safeNumber(action && action.cost), 0);
}

class ToolingMaintenanceSummary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            records: [],
            pendingApprovals: [],
            pendingApprovalsMap: {},
            keyword: '',
            statusFilter: 'all',
            loading: false,
            error: '',
            isAdmin: false,
            planningKpisByPeriod: {},
            toolingByNumber: {},
            toolingLookupError: false
        };
        this.planningRequestsInFlight = {};

        this.columns = [
            {
                name: 'Ticket ID',
                selector: row => row.repairTicketId,
                sortable: true,
                minWidth: '190px',
                cell: row => (
                    <a
                        href="#"
                        style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                        onClick={(event) => {
                            event.preventDefault();
                            if (this.props.navigate && row.id) {
                                this.props.navigate('/tooling/maintenance-summary/' + row.id);
                            }
                        }}
                    >
                        {row.repairTicketId || ('Request #' + row.id)}
                    </a>
                ),
                ignoreRowClick: true
            },
            {
                name: 'Part No.',
                selector: row => row.partNo,
                sortable: true,
                minWidth: '260px'
            },
            {
                name: 'Tooling No.',
                selector: row => row.toolingNo,
                sortable: true,
                minWidth: '140px'
            },
            {
                name: 'Tooling Type',
                selector: row => row.toolingType,
                sortable: true,
                minWidth: '140px'
            },
            {
                name: 'Reason Category',
                selector: row => row.reasonCategory,
                sortable: true,
                minWidth: '185px',
                wrap: true
            },
            {
                name: 'Downtime (Hrs)',
                selector: row => row.downtimeHours,
                sortable: true,
                right: true,
                minWidth: '145px'
            },
            {
                name: 'Cost (MYR)',
                selector: row => row.sparesCost,
                sortable: true,
                right: true,
                minWidth: '130px',
                cell: row => row.sparesCost ? row.sparesCost.toFixed(2) : ''
            },
            {
                name: 'Status',
                selector: row => row.statusLabel,
                sortable: true,
                minWidth: '170px',
                cell: row => this.renderStatus(row)
            },
            {
                name: 'Reported',
                selector: row => row.requestedAtLabel,
                sortable: true,
                minWidth: '165px'
            }
        ];
    }

    componentDidMount() {
        const userData = localStorage.getItem('user');
        if (!userData) return;

        const user = JSON.parse(userData);
        this.setState({ isAdmin: isAdmin(user) }, () => {
            this.fetchPendingApprovals().then(() => this.fetchRecords());
        });
    }

    normalizeRecord = (item, toolingByNumber = {}) => {
        const id = item && item.id ? parseInt(item.id, 10) : null;
        const pendingMap = this.state.pendingApprovalsMap || {};
        const pendingForMe = id != null && !!pendingMap[id];
        const statusLabel = pendingForMe ? 'Pending Your Approval' : approvalStatusLabel(item);
        const downtimeHours = calculateDowntimeHours(item);
        const toolingNo = safeString(item.toolingNo);
        const tooling = toolingByNumber[getToolingKey(toolingNo)];

        return {
            id,
            repairTicketId: safeString(item.repairTicketId),
            toolingNo,
            toolingId: safeString(tooling && tooling.id),
            partNo: safeString(tooling && tooling.partNo).trim() || 'N/A',
            toolingType: safeString(item.toolingType),
            reportedBy: safeString(item.reportedBy),
            requestedAt: item.requestedAt,
            requestedAtLabel: formatDateTime(item.requestedAt),
            processSubmittedAt: item.processSubmittedAt,
            reasonCategory: getReasonCategory(item),
            downtimeHours: downtimeHours === '' ? '' : Number(downtimeHours),
            sparesCost: calculateSparesCost(item),
            statusLabel,
            approve_status: item.approve_status,
            processStatus: safeString(item.processStatus),
            pendingForMe
        };
    }

    fetchPendingApprovals = () => {
        const userData = localStorage.getItem('user');
        if (!userData) return Promise.resolve();

        const user = JSON.parse(userData);
        const userId = user && user.id;
        const accountId = user && user.accountId;
        if (!userId) return Promise.resolve();

        const url = isAdmin(user) && accountId
            ? `${config.apiUrl}/getAllPendingApprovals?account_id=${accountId}&record_type=${RECORD_TYPE}`
            : `${config.apiUrl}/getPendingApprovals?approver_id=${userId}&record_type=${RECORD_TYPE}`;

        return apiFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => {
                if (!text || text.trim() === '') return [];
                try {
                    const data = JSON.parse(text);
                    return Array.isArray(data) ? data : [];
                } catch (_error) {
                    return [];
                }
            }))
            .then(approvals => {
                const currentUserId = parseInt(userId, 10);
                const filteredApprovals = approvals.filter(approval => {
                    const pendingApproverId = parseInt(approval.pending_approver_id, 10);
                    return !isNaN(pendingApproverId) && pendingApproverId === currentUserId;
                });
                const pendingApprovalsMap = {};
                const pendingApprovals = filteredApprovals
                    .map(approval => parseInt(approval.record_id || approval.id, 10))
                    .filter(recordId => !isNaN(recordId));

                pendingApprovals.forEach(recordId => {
                    pendingApprovalsMap[recordId] = true;
                });

                this.setState({
                    pendingApprovals,
                    pendingApprovalsMap
                });
            })
            .catch(error => {
                console.error('Error fetching tooling maintenance approvals:', error);
                this.setState({ pendingApprovals: [], pendingApprovalsMap: {} });
            });
    }

    fetchRecords = (forcePlanningRefresh = false) => {
        const userData = localStorage.getItem('user');
        if (!userData) return Promise.resolve();

        const user = JSON.parse(userData);
        const accountId = user && user.accountId;
        if (!accountId) return Promise.resolve();

        const keyword = encodeURIComponent(this.state.keyword || '');
        this.setState({ loading: true, error: '' });

        const maintenanceRequest = fetch(`${config.apiUrl}/getToolingMaintenanceRequestsByCompany?company=${accountId}&keyword=${keyword}&limit=${REQUEST_LIMIT}&offset=0`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => {
                const body = text ? JSON.parse(text) : {};
                if (!response.ok) {
                    throw new Error(body.error || 'Failed to load tooling maintenance requests');
                }
                return body;
            }));
        const toolingRequest = fetch(`${config.apiUrl}/getToolingByCompany?company=${accountId}&keyword=&limit=${REQUEST_LIMIT}&offset=0`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
        })
            .then(response => response.text().then(text => {
                const body = text ? JSON.parse(text) : {};
                if (!response.ok) {
                    throw new Error(body.error || 'Failed to load tooling records');
                }
                return buildToolingLookup(body);
            }))
            .catch(error => {
                console.error('Error fetching tooling records:', error);
                return null;
            });

        return Promise.all([maintenanceRequest, toolingRequest])
            .then(([body, toolingByNumber]) => {
                const data = Array.isArray(body) ? body : (body.data || []);
                const resolvedToolingByNumber = toolingByNumber || {};
                this.setState({
                    records: data.map(item => this.normalizeRecord(item, resolvedToolingByNumber)),
                    loading: false,
                    toolingByNumber: resolvedToolingByNumber,
                    toolingLookupError: !toolingByNumber
                }, () => {
                    this.loadPlanningOperatingHours(this.getFilteredRecords(), forcePlanningRefresh);
                });
            })
            .catch(error => {
                console.error('Error fetching tooling maintenance records:', error);
                this.setState({
                    records: [],
                    loading: false,
                    error: error.message || 'Failed to load tooling maintenance requests'
                });
            });
    }

    loadPlanningOperatingHours = (records, forceRefresh = false) => {
        const period = getDowntimePeriod(records);
        if (!period || this.state.toolingLookupError) return;

        const cachedKpi = this.state.planningKpisByPeriod[period.key];
        if (this.planningRequestsInFlight[period.key] || (cachedKpi && !forceRefresh)) return;

        const hasMaintenanceTooling = records.some(record => safeString(record && record.toolingId));
        if (!hasMaintenanceTooling) {
            this.setState(previousState => ({
                planningKpisByPeriod: {
                    ...previousState.planningKpisByPeriod,
                    [period.key]: { loading: false, totalOperatingHours: 0, eligibleMaintenanceEvents: 0, error: false }
                }
            }));
            return;
        }

        const hasSuccessfulValue = cachedKpi
            && !cachedKpi.error
            && Number.isFinite(cachedKpi.totalOperatingHours)
            && Number.isFinite(cachedKpi.eligibleMaintenanceEvents);
        this.planningRequestsInFlight[period.key] = true;

        this.setState(previousState => ({
            planningKpisByPeriod: {
                ...previousState.planningKpisByPeriod,
                [period.key]: {
                    loading: true,
                    totalOperatingHours: hasSuccessfulValue ? cachedKpi.totalOperatingHours : null,
                    eligibleMaintenanceEvents: hasSuccessfulValue ? cachedKpi.eligibleMaintenanceEvents : null,
                    error: false
                }
            }
        }));

        const userData = localStorage.getItem('user');
        const user = userData ? JSON.parse(userData) : {};
        const accountId = user && user.accountId;
        const planningUrl = `${config.apiUrl}/getPlanning?company=${accountId}&start=${period.startDate}&end=${period.endDate}&process=&machine=`;

        fetch(planningUrl, { method: 'GET' })
            .then(response => {
                if (!response.ok) throw new Error('Failed to load planning events');
                return response.text();
            })
            .then(text => text ? JSON.parse(text) : [])
            .then(events => {
                const planningEvents = Array.isArray(events) ? events : [];
                const jobIds = [...new Set(planningEvents.map(event => event.jobID).filter(Boolean))];
                return Promise.all(jobIds.map(jobId =>
                    fetch(`${config.apiUrl}/getJobByFilter?id=${jobId}`, { method: 'GET' })
                        .then(response => {
                            if (!response.ok) throw new Error('Failed to load planning job');
                            return response.text();
                        })
                        .then(text => text ? JSON.parse(text) : {})
                ));
            })
            .then(jobs => {
                const mtbfMetrics = calculateMtbfMetrics(records, jobs);
                this.setState(previousState => ({
                    planningKpisByPeriod: {
                        ...previousState.planningKpisByPeriod,
                        [period.key]: { loading: false, ...mtbfMetrics, error: false }
                    }
                }), () => {
                    delete this.planningRequestsInFlight[period.key];
                });
            })
            .catch(error => {
                console.error('Error fetching planning operating hours:', error);
                this.setState(previousState => ({
                    planningKpisByPeriod: {
                        ...previousState.planningKpisByPeriod,
                        [period.key]: { loading: false, totalOperatingHours: 0, eligibleMaintenanceEvents: 0, error: true }
                    }
                }), () => {
                    delete this.planningRequestsInFlight[period.key];
                });
            });
    }

    refresh = () => {
        this.fetchPendingApprovals().then(() => this.fetchRecords(true));
    }

    renderStatus(row) {
        let color = '#6c757d';
        if (row.statusLabel === 'Approved') color = '#218838';
        if (row.statusLabel === 'Rejected') color = '#dc3545';
        if (row.statusLabel === 'Pending Approval' || row.statusLabel === 'Pending Your Approval') color = '#c77800';

        return <span style={{ color, fontWeight: 600 }}>{row.statusLabel}</span>;
    }

    getFilteredRecords() {
        const { records, statusFilter, pendingApprovals } = this.state;
        if (statusFilter === 'pending') {
            const pendingSet = new Set(pendingApprovals);
            return records.filter(record => pendingSet.has(record.id));
        }
        if (statusFilter === 'approved') {
            return records.filter(record => Number(record.approve_status) === 1);
        }
        if (statusFilter === 'rejected') {
            return records.filter(record => Number(record.approve_status) === 2);
        }
        if (statusFilter === 'in-process') {
            return records.filter(record => Number(record.approve_status) !== 1 && Number(record.approve_status) !== 2);
        }
        return records;
    }

    renderKpi(label, value) {
        return (
            <Col sm={12} md={4} style={{ marginBottom: '12px' }}>
                <div style={{ border: '1px solid #e1e5ee', borderRadius: '6px', padding: '16px', background: '#fff' }}>
                    <div style={{ color: '#6c757d', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ color: '#1f2933', fontSize: '28px', fontWeight: 700, marginTop: '6px' }}>{value}</div>
                </div>
            </Col>
        );
    }

    render() {
        const accessDenied = redirectUnlessTooling(localStorage.getItem('user'));
        if (accessDenied) return accessDenied;

        const records = this.getFilteredRecords();
        const totalDowntime = records.reduce((total, record) => total + safeNumber(record.downtimeHours), 0);
        const totalCost = records.reduce((total, record) => total + safeNumber(record.sparesCost), 0);
        const period = getDowntimePeriod(records);
        const planningKpi = period ? this.state.planningKpisByPeriod[period.key] || {} : {};
        const maintenanceEvents = records.length;
        const hasPlanningValue = Number.isFinite(planningKpi.totalOperatingHours);
        const totalOperatingHours = hasPlanningValue ? planningKpi.totalOperatingHours : 0;
        const mtbfMaintenanceEvents = safeNumber(planningKpi.eligibleMaintenanceEvents);
        const mtbf = mtbfMaintenanceEvents ? totalOperatingHours / mtbfMaintenanceEvents : 0;
        const mttr = maintenanceEvents ? totalDowntime / maintenanceEvents : 0;
        let mtbfValue = '0 hrs';
        if (maintenanceEvents) {
            if (!period || this.state.toolingLookupError || planningKpi.error) {
                mtbfValue = 'Unavailable';
            } else if (planningKpi.loading && !hasPlanningValue) {
                mtbfValue = 'Loading...';
            } else if (!planningKpi.loading && planningKpi.eligibleMaintenanceEvents !== null && planningKpi.eligibleMaintenanceEvents !== undefined && !mtbfMaintenanceEvents) {
                mtbfValue = 'Unavailable';
            } else if (!hasPlanningValue || !mtbfMaintenanceEvents) {
                mtbfValue = 'Loading...';
            } else {
                mtbfValue = formatHours(mtbf);
            }
        }

        return (
            <div className="container">
                <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '4px' }}>Tooling Asset Maintenance & Downtime Summary</h4>
                    <div style={{ color: '#6c757d' }}>Aggregated production-ready tracker for structural failure and maintenance KPIs.</div>
                </div>

                <Row>
                    {this.renderKpi('Total Maintenance Events', records.length)}
                    {this.renderKpi('Total Accumulated Downtime', totalDowntime ? totalDowntime.toFixed(2) + ' hrs' : '0 hrs')}
                    {this.renderKpi('Total Spares Expenditure', 'MYR ' + totalCost.toFixed(2))}
                    {this.renderKpi('Mean Time Between Failures (MTBF)', mtbfValue)}
                    {this.renderKpi('Mean Time To Repair (MTTR)', maintenanceEvents ? formatHours(mttr) : '0 hrs')}
                </Row>

                <div style={{ margin: '18px 0', padding: '15px 0' }}>
                    <Row>
                        <Col sm={12} md={5} style={{ marginBottom: '10px' }}>
                            <FormControl
                                type="text"
                                placeholder="Ticket ID, tooling no, part no, type, reporter"
                                onChange={event => this.setState({ keyword: event.target.value })}
                                onKeyPress={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault();
                                        this.refresh();
                                    }
                                }}
                                value={this.state.keyword}
                            />
                        </Col>
                        <Col sm={6} md={3} style={{ marginBottom: '10px' }}>
                            <FormControl
                                as="select"
                                value={this.state.statusFilter}
                                onChange={event => this.setState({ statusFilter: event.target.value }, () => {
                                    this.loadPlanningOperatingHours(this.getFilteredRecords());
                                })}
                            >
                                <option value="all">All Records</option>
                                <option value="pending">Pending My Approval</option>
                                <option value="in-process">In Process</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </FormControl>
                        </Col>
                        <Col sm={6} md={2} style={{ marginBottom: '10px' }}>
                            <Button variant="primary" onClick={this.refresh} style={{ width: '100%' }}>Search</Button>
                        </Col>
                        <Col sm={6} md={2} style={{ marginBottom: '10px' }}>
                            <Button variant="outline-secondary" onClick={this.refresh} style={{ width: '100%' }}>Refresh</Button>
                        </Col>
                    </Row>
                </div>

                <h5 style={{ marginBottom: '12px' }}>Historical Tooling Repair & Maintenance Ledger</h5>
                {this.state.error ? <div className="alert alert-danger">{this.state.error}</div> : null}
                <DataTable
                    columns={this.columns}
                    data={records}
                    progressPending={this.state.loading}
                    progressComponent={<DatacoLoadingIndicator loading={this.state.loading} size="compact" label="Loading tooling maintenance requests" />}
                    pagination
                    highlightOnHover
                    striped
                    persistTableHead
                    noDataComponent="No tooling maintenance records found"
                />
            </div>
        );
    }
}

function withNavigate(Component) {
    return (props) => {
        const navigate = useNavigate();
        return <Component {...props} navigate={navigate} />;
    };
}

const ToolingMaintenanceSummaryWithNavigate = withNavigate(ToolingMaintenanceSummary);
export {
    ToolingMaintenanceSummaryWithNavigate as ToolingMaintenanceSummary,
    buildToolingLookup,
    calculateMtbfMetrics,
    getReasonCategory,
};
